/** Authored Markdown subset: headings, paragraphs, lists, tables, bold, links and hard breaks.
 * List continuation lines are indented by the marker width. No HTML or error recovery.
 */
export type Inline =
  | { type: 'text' | 'strong'; text: string }
  | { type: 'link'; text: string; href: string }
  | { type: 'break' }
export type Block =
  | { type: 'heading'; level: number; content: Inline[] }
  | { type: 'paragraph'; content: Inline[] }
  | { type: 'list'; ordered: boolean; items: Block[][] }
  | { type: 'table'; rows: Inline[][][] }

export function parseMarkdown(source: string): Block[] {
  const lines = source.split('\n')
  const blocks: Block[] = []
  let index = 0
  while (index < lines.length) {
    const line = lines[index]
    if (!line.trim()) {
      index++
      continue
    }
    const heading = /^(#{1,3}) (.+)$/.exec(line)
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, content: inline(heading[2]) })
      index++
      continue
    }
    const firstItem = /^(\d+\. |- )(.*)$/.exec(line)
    if (firstItem) {
      const ordered = firstItem[1] != '- '
      const items: Block[][] = []
      while (index < lines.length) {
        const item = /^(\d+\. |- )(.*)$/.exec(lines[index])
        if (!item || (item[1] != '- ') != ordered) break
        const content = [item[2]]
        const indent = ' '.repeat(item[1].length)
        index++
        while (index < lines.length) {
          if (lines[index].startsWith(indent)) {
            content.push(lines[index].slice(indent.length))
            index++
          } else if (!lines[index].trim()) {
            content.push('')
            index++
          } else break
        }
        items.push(parseMarkdown(content.join('\n')))
      }
      blocks.push({ type: 'list', ordered, items })
      continue
    }
    if (line.startsWith('|')) {
      const rows: Inline[][][] = []
      const cells = (row: string) => {
        if (!row.endsWith('|')) throw new Error('Unclosed Markdown table: ' + row)
        return row
          .slice(1, -1)
          .split('|')
          .map((cell) => cell.trim())
      }
      const header = cells(line)
      const separator = cells(lines[index + 1] ?? '')
      if (separator.length != header.length || !separator.every((cell) => /^-+$/.test(cell)))
        throw new Error('Invalid Markdown table: ' + line)
      rows.push(header.map(inline))
      index += 2
      while (index < lines.length && lines[index].startsWith('|')) {
        const row = cells(lines[index++])
        if (row.length != header.length) throw new Error('Unequal Markdown table columns')
        rows.push(row.map(inline))
      }
      blocks.push({ type: 'table', rows })
      continue
    }
    if (/^\s|^#{4}|^>|^```/.test(line)) throw new Error('Unsupported Markdown block: ' + line)
    const paragraph = [line]
    index++
    while (
      index < lines.length &&
      lines[index].trim() &&
      !/^(#|\||\d+\. |\- )/.test(lines[index])
    ) {
      paragraph.push(lines[index++])
    }
    blocks.push({ type: 'paragraph', content: inline(paragraph.join('\n')) })
  }
  return blocks
}

function inline(source: string): Inline[] {
  const tokens: Inline[] = []
  const pattern = /\*\*([^*]+)\*\*|\[([^\]\n]+)\]\((https:\/\/[^\s)]+)\)|(\\\n)|([^*_`<\[\]\\]+)/y
  let position = 0
  while (position < source.length) {
    pattern.lastIndex = position
    const token = pattern.exec(source)
    if (!token) throw new Error('Unsupported Markdown inline: ' + source.slice(position))
    if (token[1]) tokens.push({ type: 'strong', text: token[1] })
    else if (token[2]) tokens.push({ type: 'link', text: token[2], href: token[3] })
    else if (token[4]) tokens.push({ type: 'break' })
    else tokens.push({ type: 'text', text: token[5].replace(/\n/g, ' ') })
    position = pattern.lastIndex
  }
  return tokens
}
